import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import PlatformDashboard from './PlatformDashboard';
import {
  Users,
  Building2,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Activity,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Super Admin gets a completely different, platform-only dashboard —
  // no payroll/attendance/employee stats here.
  if (user?.role === 'super_admin') {
    return <PlatformDashboard />;
  }

  return <TenantDashboard user={user} navigate={navigate} />;
};

const TenantDashboard = ({ user, navigate }) => {
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/dashboard/stats`, {
        withCredentials: true,
      });
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      label: 'Total Employees',
      value: stats.total_employees || 0,
      icon: Users,
      color: 'bg-blue-50 text-blue-600',
      show: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin'].includes(user?.role),
    },
    {
      label: 'Active Employees',
      value: stats.active_employees || 0,
      icon: CheckCircle,
      color: 'bg-green-50 text-green-600',
      show: ['tenant_admin', 'hr_admin', 'payroll_admin', 'attendance_admin'].includes(user?.role),
    },
    {
      label: 'Users',
      value: stats.total_users || 0,
      icon: Users,
      color: 'bg-sky-50 text-sky-600',
      show: ['tenant_admin', 'hr_admin'].includes(user?.role),
    },
    {
      label: 'Companies',
      value: stats.total_companies || 0,
      icon: Building2,
      color: 'bg-purple-50 text-purple-600',
      show: ['tenant_admin', 'hr_admin'].includes(user?.role),
    },
    {
      label: 'Pending Approvals',
      value: stats.pending_approvals || 0,
      icon: AlertCircle,
      color: 'bg-amber-50 text-amber-600',
      show: true,
    },
    {
      label: 'Unread Notifications',
      value: stats.unread_notifications || 0,
      icon: Activity,
      color: 'bg-red-50 text-red-600',
      show: true,
    },
  ];

  const filteredCards = statCards.filter((card) => card.show);

  return (
    <MainLayout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              Welcome back, {user?.name}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Here's what's happening with your organization today.
            </p>
          </div>
          {stats.scope_label && (
            <div data-testid="dashboard-scope-badge"
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-50 border border-indigo-200 text-sm">
              <Building2 className="w-4 h-4 text-indigo-600" />
              <div>
                <p className="text-xs text-indigo-500 font-medium uppercase tracking-wider">Your Scope</p>
                <p className="font-semibold text-indigo-900">{stats.scope_label}</p>
              </div>
            </div>
          )}
        </div>

        {/* Stats Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-lg p-6 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-slate-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="dashboard-stats-grid">
            {filteredCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <div
                  key={index}
                  data-testid={`stat-card-${card.label.toLowerCase().replace(/\s/g, '-')}`}
                  className="bg-white border border-slate-200 rounded-lg p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                        {card.label}
                      </p>
                      <p className="mt-2 text-3xl font-semibold text-slate-900 font-mono">
                        {card.value}
                      </p>
                    </div>
                    <div className={`p-3 rounded-lg ${card.color}`}>
                      <Icon className="w-6 h-6" strokeWidth={1.5} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {['super_admin', 'tenant_admin', 'hr_admin'].includes(user?.role) && (
              <>
                <button data-testid="quick-action-add-user" onClick={() => navigate('/users')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
                  <p className="font-medium text-slate-900">Add New User</p>
                  <p className="text-xs text-slate-500 mt-1">Create a new user account</p>
                </button>
                <button data-testid="quick-action-add-company" onClick={() => navigate('/companies')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
                  <p className="font-medium text-slate-900">Add Company</p>
                  <p className="text-xs text-slate-500 mt-1">Register a new company</p>
                </button>
              </>
            )}
            <button data-testid="quick-action-view-notifications" onClick={() => navigate('/notifications')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
              <p className="font-medium text-slate-900">View Notifications</p>
              <p className="text-xs text-slate-500 mt-1">Check latest updates</p>
            </button>
            <button data-testid="quick-action-view-approvals" onClick={() => navigate('/action-inbox')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
              <p className="font-medium text-slate-900">Pending Approvals</p>
              <p className="text-xs text-slate-500 mt-1">Review pending items</p>
            </button>
            {['super_admin', 'tenant_admin', 'hr_admin'].includes(user?.role) && (
              <button data-testid="quick-action-employees" onClick={() => navigate('/employees')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
                <p className="font-medium text-slate-900">Manage Employees</p>
                <p className="text-xs text-slate-500 mt-1">View, add, or export employees</p>
              </button>
            )}
            {['super_admin', 'tenant_admin', 'payroll_admin', 'hr_admin'].includes(user?.role) && (
              <button data-testid="quick-action-payroll" onClick={() => navigate('/payroll/run')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
                <p className="font-medium text-slate-900">Run Payroll</p>
                <p className="text-xs text-slate-500 mt-1">Process monthly payroll</p>
              </button>
            )}
            {['super_admin', 'tenant_admin', 'payroll_admin', 'hr_admin', 'finance_reviewer'].includes(user?.role) && (
              <button data-testid="quick-action-reports" onClick={() => navigate('/reports')} className="p-4 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors text-left">
                <p className="font-medium text-slate-900">Reports & Downloads</p>
                <p className="text-xs text-slate-500 mt-1">Generate and download reports</p>
              </button>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold text-slate-900 mb-4">Recent System Activity</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-slate-900">System initialization complete</p>
                  <p className="text-xs text-slate-500">All modules loaded successfully</p>
                </div>
              </div>
              <span className="text-xs text-slate-400">Just now</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Multi-tenant architecture active</p>
                  <p className="text-xs text-slate-500">Tenant isolation verified</p>
                </div>
              </div>
              <span className="text-xs text-slate-400">2 min ago</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Audit trail enabled</p>
                  <p className="text-xs text-slate-500">All actions being logged</p>
                </div>
              </div>
              <span className="text-xs text-slate-400">5 min ago</span>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
